/**
 * Подписка на поток монитора (SSE) с автопереподключением.
 *
 * EventSource умеет переподключаться сам, но без контроля паузы: при лежащем
 * сервере он долбит его раз в пару секунд. Поэтому переподключением управляем
 * вручную — экспоненциальный backoff с джиттером.
 */

import type { MonitorEvent } from '@cdf/shared';

import { EVENTS_URL } from './client';

export type MonitorConnectionState = 'connecting' | 'open' | 'offline';

export interface MonitorSubscription {
  close(): void;
}

interface MonitorHandlers {
  onEvent(event: MonitorEvent): void;
  onStateChange(state: MonitorConnectionState): void;
}

const BASE_DELAY_MS = 1_000;
const MAX_DELAY_MS = 30_000;

/** Разбирает payload SSE в MonitorEvent, отбрасывая мусор. */
function parseEvent(data: string, fallbackType?: string): MonitorEvent | null {
  let payload: unknown;
  try {
    payload = JSON.parse(data);
  } catch {
    return null;
  }
  if (typeof payload !== 'object' || payload === null) return null;

  const record = payload as Record<string, unknown>;
  const type = typeof record['type'] === 'string' ? record['type'] : fallbackType;
  if (typeof type !== 'string') return null;

  return { ...record, type } as MonitorEvent;
}

const NAMED_TYPES = ['tick', 'alert', 'plan_updated', 'error'] as const;

export function subscribeMonitor(handlers: MonitorHandlers): MonitorSubscription {
  let source: EventSource | null = null;
  let retryTimer: number | undefined;
  let attempt = 0;
  let closed = false;

  const emit = (data: string, fallbackType?: string): void => {
    const event = parseEvent(data, fallbackType);
    if (event) handlers.onEvent(event);
  };

  const scheduleReconnect = (): void => {
    if (closed) return;
    const delay = Math.min(MAX_DELAY_MS, BASE_DELAY_MS * 2 ** attempt);
    // Джиттер, чтобы после падения сервера клиенты не пришли одной волной.
    const jittered = delay * (0.7 + Math.random() * 0.6);
    attempt += 1;
    retryTimer = window.setTimeout(connect, jittered);
  };

  const connect = (): void => {
    if (closed) return;
    handlers.onStateChange('connecting');

    const es = new EventSource(EVENTS_URL, { withCredentials: true });
    source = es;

    es.onopen = () => {
      attempt = 0;
      handlers.onStateChange('open');
    };

    es.onmessage = (event) => emit(event.data);

    for (const type of NAMED_TYPES) {
      es.addEventListener(type, (event) => {
        // У ошибки соединения нет data — только у именованного события сервера.
        const data = (event as MessageEvent<string>).data;
        if (typeof data === 'string') emit(data, type);
      });
    }

    es.onerror = () => {
      if (es.readyState === EventSource.CLOSED || es.readyState === EventSource.CONNECTING) {
        es.close();
        if (source === es) source = null;
        handlers.onStateChange('offline');
        scheduleReconnect();
      }
    };
  };

  // Возврат сети — повод не ждать конца backoff.
  const onOnline = (): void => {
    if (closed || source) return;
    window.clearTimeout(retryTimer);
    attempt = 0;
    connect();
  };
  window.addEventListener('online', onOnline);

  connect();

  return {
    close(): void {
      closed = true;
      window.clearTimeout(retryTimer);
      window.removeEventListener('online', onOnline);
      source?.close();
      source = null;
    },
  };
}
